// v119.78 — Product Availability Panel.
//
// Lets the manager mark products as "out of stock" / "discontinued" so the
// WhatsApp Customer AI stops suggesting them. Renders every product from
// datasets.prod merged with the per-product availability flag stored in
// the separate `product_availability` collection (so re-uploading the
// products file never wipes the manager's choices).
//
// UX:
//   • Search box to quickly find a product among hundreds.
//   • Status filter pills (all / available / unavailable).
//   • Inline toggle switch per row + reason input that appears when toggled OFF.
//   • Bulk select + "mark selected as unavailable" action with a single
//     shared reason.
//   • Bottom "💾 Save All" — sends one PUT to /api/products/availability.
//
// Backend: GET/PUT /api/products/availability, POST .../toggle.

import { useEffect, useMemo, useState } from "react";
import { api } from "./lib/api";
import { toast } from "sonner";

export default function ProductAvailabilityPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState({}); // {name: true}
  const [bulkReason, setBulkReason] = useState("");
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/products/availability");
      setRows(r.data?.items || []);
      setDirty(false);
      setSelected({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر تحميل المنتجات");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === "available" && !r.available) return false;
      if (filter === "unavailable" && r.available) return false;
      if (!q) return true;
      return (r.name || "").toLowerCase().includes(q)
        || (r.category || "").toLowerCase().includes(q);
    });
  }, [rows, search, filter]);

  const stats = useMemo(() => {
    const total = rows.length;
    const off = rows.filter((r) => !r.available).length;
    return { total, off, on: total - off };
  }, [rows]);

  const setRowField = (name, k, v) => {
    setRows((p) => p.map((r) => (r.name === name ? { ...r, [k]: v } : r)));
    setDirty(true);
  };

  const toggleSelect = (name) => {
    setSelected((p) => ({ ...p, [name]: !p[name] }));
  };

  const selectAllFiltered = (sel) => {
    const next = { ...selected };
    filtered.forEach((r) => { next[r.name] = sel; });
    setSelected(next);
  };

  const applyBulkUnavailable = () => {
    const names = Object.keys(selected).filter((n) => selected[n]);
    if (!names.length) {
      toast.error("اختر منتجات أولاً");
      return;
    }
    const reason = (bulkReason || "").trim();
    setRows((p) => p.map((r) => (
      selected[r.name] ? { ...r, available: false, reason: reason || r.reason } : r
    )));
    setSelected({});
    setBulkReason("");
    setDirty(true);
    toast.success(`تم تحديد ${names.length} منتج كغير متاح`);
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const items = rows.map((r) => ({
        name: r.name,
        available: !!r.available,
        reason: (r.reason || "").slice(0, 200),
      }));
      const r = await api.put("/products/availability", { items });
      toast.success(`تم الحفظ — متوقف: ${r.data?.marked_unavailable || 0}، متاح: ${r.data?.marked_available || 0}`);
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحفظ");
    }
    setSaving(false);
  };

  return (
    <div data-testid="product-availability-panel" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Header card with stats + bulk actions */}
      <div className="card" style={{ padding: 14, background: "linear-gradient(135deg,#fff7ed 0%,#ffedd5 100%)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <div style={{ fontSize: 28 }}>📦</div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#7c2d12" }}>
              توقّف المنتجات
            </div>
            <div style={{ fontSize: 11.5, color: "var(--brown-deep)", marginTop: 2 }}>
              المنتجات «❌ غير المتاحة» ما يقترحها مساعد العملاء على WhatsApp، ولو سأل عنها العميل يعتذر بأدب ويقترح بديل.
            </div>
          </div>
          <div style={{ display: "flex", gap: 18, fontSize: 12, fontWeight: 700 }}>
            <div data-testid="pa-stat-total">📦 الكل: {stats.total}</div>
            <div style={{ color: "#16a34a" }} data-testid="pa-stat-on">✅ متاح: {stats.on}</div>
            <div style={{ color: "#dc2626" }} data-testid="pa-stat-off">❌ متوقف: {stats.off}</div>
          </div>
        </div>
      </div>

      {/* Search + filter pills */}
      <div className="card" style={{ padding: 12 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input
            data-testid="pa-search"
            placeholder="🔍 ابحث باسم المنتج أو الفئة..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="sim-input" style={{ flex: 1, minWidth: 220 }}
          />
          {[
            { id: "all", label: "الكل", color: "#374151" },
            { id: "available", label: "✅ متاح", color: "#16a34a" },
            { id: "unavailable", label: "❌ متوقف", color: "#dc2626" },
          ].map((f) => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              data-testid={`pa-filter-${f.id}`}
              style={{
                padding: "8px 14px", borderRadius: 6, fontSize: 12.5, fontWeight: 700,
                border: `1.5px solid ${f.color}`,
                background: filter === f.id ? f.color : "#fff",
                color: filter === f.id ? "#fff" : f.color, cursor: "pointer",
              }}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Bulk action row */}
        <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button onClick={() => selectAllFiltered(true)} className="btn btn-soft"
            data-testid="pa-select-all" style={{ fontSize: 11.5 }}>
            ☑ تحديد الكل المعروض
          </button>
          <button onClick={() => selectAllFiltered(false)} className="btn btn-soft"
            data-testid="pa-deselect-all" style={{ fontSize: 11.5 }}>
            ☐ إلغاء التحديد
          </button>
          <input data-testid="pa-bulk-reason"
            placeholder="سبب جماعي (مثل: نفد المخزون)"
            value={bulkReason} onChange={(e) => setBulkReason(e.target.value)}
            className="sim-input" style={{ flex: 1, minWidth: 180 }}
          />
          <button onClick={applyBulkUnavailable}
            data-testid="pa-apply-bulk"
            style={{
              padding: "8px 14px", borderRadius: 6, fontSize: 12, fontWeight: 700,
              border: "none", background: "#dc2626", color: "#fff", cursor: "pointer",
            }}>
            ❌ تعليم المختار كغير متاح
          </button>
        </div>
      </div>

      {/* Products table */}
      <div className="card" style={{ padding: 0, overflow: "auto", maxHeight: 600 }}>
        {loading ? (
          <div style={{ padding: 30, textAlign: "center", color: "var(--muted)" }}>جارٍ التحميل…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center", color: "var(--muted)" }}>
            {rows.length === 0 ? "لا توجد منتجات — ارفع ملف المنتجات من تبويب المنتجات أولاً" : "لا توجد نتائج للبحث"}
          </div>
        ) : (
          <table data-testid="pa-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead>
              <tr style={{ background: "#f9fafb", position: "sticky", top: 0 }}>
                <th style={thStyle}></th>
                <th style={thStyle}>المنتج</th>
                <th style={thStyle}>الفئة</th>
                <th style={thStyle}>الحالة</th>
                <th style={thStyle}>السبب (إن كان متوقف)</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.name}
                  data-testid={`pa-row-${r.name}`}
                  style={{
                    borderTop: "1px solid var(--border)",
                    background: r.available ? "#fff" : "#fef2f2",
                  }}>
                  <td style={tdStyle}>
                    <input type="checkbox"
                      data-testid={`pa-select-${r.name}`}
                      checked={!!selected[r.name]}
                      onChange={() => toggleSelect(r.name)} />
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600 }}>{r.name}</div>
                    {r.orphan && (
                      <div style={{ fontSize: 10, color: "var(--muted)" }}>(غير موجود بالكاتالوج الحالي)</div>
                    )}
                  </td>
                  <td style={{ ...tdStyle, color: "var(--muted)" }}>{r.category || "—"}</td>
                  <td style={tdStyle}>
                    <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                      <input type="checkbox"
                        data-testid={`pa-toggle-${r.name}`}
                        checked={!!r.available}
                        onChange={(e) => setRowField(r.name, "available", e.target.checked)}
                        style={{ width: 18, height: 18 }} />
                      <span style={{
                        fontSize: 11.5, fontWeight: 700,
                        color: r.available ? "#16a34a" : "#dc2626",
                      }}>
                        {r.available ? "✅ متاح" : "❌ متوقف"}
                      </span>
                    </label>
                  </td>
                  <td style={tdStyle}>
                    <input
                      data-testid={`pa-reason-${r.name}`}
                      placeholder={r.available ? "—" : "مثل: نفد المخزون"}
                      value={r.reason || ""}
                      onChange={(e) => setRowField(r.name, "reason", e.target.value)}
                      disabled={r.available}
                      className="sim-input" style={{ width: "100%", fontSize: 12 }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Save button (sticky) */}
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <button onClick={saveAll} disabled={saving || !dirty}
          data-testid="pa-save"
          style={{
            padding: "10px 22px", borderRadius: 8, fontSize: 13, fontWeight: 800,
            border: "none", background: dirty ? "#16a34a" : "#94a3b8", color: "#fff",
            cursor: dirty ? "pointer" : "not-allowed",
          }}>
          {saving ? "جارٍ الحفظ…" : dirty ? "💾 احفظ كل التغييرات" : "✓ محفوظ"}
        </button>
        {dirty && (
          <span style={{ fontSize: 11.5, color: "#7c2d12" }} data-testid="pa-dirty-hint">
            ⚠ تغييرات لم تُحفظ بعد
          </span>
        )}
      </div>
    </div>
  );
}

const thStyle = { padding: "10px 12px", fontSize: 11.5, color: "var(--muted)", textAlign: "right", fontWeight: 700 };
const tdStyle = { padding: "10px 12px", verticalAlign: "middle" };
